<template>
  <div class="custom-message-fields">
    <info-hint variant="primary">{{ hint }}</info-hint>
    <div class="toggle-row">
      <el-switch active-color="#e14eca" :value="active" @change="emit('update:active', $event)" />
      <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="emit('update:active', !active)">
        {{ toggleLabel }}
      </label>
    </div>
    <template v-if="active">
      <label class="text-white mt-2 d-block">Message</label>
      <base-input
        :value="message"
        :maxlength="200"
        placeholder="e.g. Closed for Christmas!"
        :error="error"
        @input="emit('update:message', $event)"
      ></base-input>
      <small v-if="display === 'badge' && message.length > 20" class="badge-length-hint">
        Badges read best with 20 characters or fewer.
      </small>
      <label class="text-white d-block mt-2">Display style</label>
      <segmented-toggle
        :value="display"
        :options="displayOptions"
        size="sm"
        @input="emit('update:display', $event)"
      />
    </template>
    <div v-if="message && active" class="custom-message-preview mt-3">
      <label class="text-white d-block mb-2">Preview</label>
      <div class="preview-frame">
        <div v-if="display === 'text'" class="preview-slot">
          <div class="preview-time">{{ previewTime }}</div>
          <div class="preview-text">{{ message }}</div>
        </div>
        <div v-else class="preview-slot">
          <span class="preview-badge">{{ message }}</span>
          <div class="preview-time">{{ previewTime }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Shared editor for the customer-facing custom message on a slot or slot
// template. Parents bind with .sync: :message.sync, :display.sync, :active.sync.
withDefaults(defineProps<{
  message: string
  display: 'text' | 'badge'
  active: boolean
  previewTime: string
  error?: string
  toggleLabel?: string
  hint?: string
}>(), {
  error: '',
  toggleLabel: 'Show message on this booking slot',
  hint: 'This text shows in addition to any other text on the customer-facing slot on the booking site.'
})

const emit = defineEmits<{
  (e: 'update:message', value: string): void
  (e: 'update:display', value: 'text' | 'badge'): void
  (e: 'update:active', value: boolean): void
}>()

const displayOptions = [
  { value: 'text', label: 'Text' },
  { value: 'badge', label: 'Badge' }
]
</script>

<script lang="ts">
export default { name: 'CustomMessageFields' }
</script>

<style lang="scss" scoped>
.toggle-row {
  display: flex;
  align-items: center;
}
.badge-length-hint {
  color: rgba(255, 255, 255, 0.7);
  display: block;
  margin-top: -8px;
  margin-bottom: 8px;
}
.custom-message-preview {
  background: rgba(30, 30, 47, 0.6);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  padding: 12px;
}
.preview-frame {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.preview-slot {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid #e14eca;
  border-radius: 0.4285rem;
  color: #e14eca;
  background: transparent;
  min-width: 140px;
  text-align: center;
}
.preview-time {
  font-size: 14px;
  font-weight: 600;
}
.preview-text {
  font-size: 11px;
  margin-top: 4px;
  word-break: normal;
  overflow-wrap: break-word;
}
.preview-badge {
  position: absolute;
  top: -10px;
  right: -8px;
  background: #e14eca;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
.white-content .custom-message-fields {
  .badge-length-hint {
    color: rgba(0, 0, 0, 0.6);
  }
  .custom-message-preview {
    background: #f8f9fa;
    border-color: rgba(0, 0, 0, 0.15);
  }
  .preview-slot {
    background: #ffffff;
  }
}
</style>
